import type { RecitalSummary } from "@/lib/api/recitals-api";

/**
 * Ce qu'un lien de récital dit ailleurs : la vignette d'une messagerie, la
 * carte d'un réseau, l'affiche qu'on en fabrique (`opengraph-image.tsx`).
 *
 * # Un seul endroit
 *
 * Les balises et l'image sont rendues par deux fichiers différents, dans deux
 * moteurs différents, à partir du même récital. Écrites deux fois, elles se
 * mettraient à diverger au premier changement de mot, et personne ne le verrait
 * : ce que lit un réseau n'apparaît sur aucun écran du produit.
 *
 * # Le fuseau est dit
 *
 * Une vignette est fabriquée par le robot d'un réseau, pas par la personne qui
 * la lira : il n'y a pas de fuseau local à prendre. On annonce donc l'heure de
 * Paris, et on l'écrit. Le compte à rebours de la salle d'attente, lui, reste
 * dans le fuseau de qui regarde (`recital-time.ts`).
 */

/** Le fuseau des heures annoncées hors du produit, écrit à côté d'elles. */
export const SHARE_TIME_ZONE = "Europe/Paris";

/** Le nom de l'interprète, ou son défaut. Jamais vide. */
export function performerName(recital: Pick<RecitalSummary, "performer">): string {
  return recital.performer.nickname ?? "Quelqu'un";
}

/** Ce qu'est cette page, en capitales, au-dessus du titre de l'affiche. */
export function shareEyebrow(recital: RecitalSummary): string {
  switch (recital.status) {
    case "live":
      return "EN DIRECT";
    case "ended":
      return "RÉCITAL TERMINÉ";
    case "cancelled":
      return "RÉCITAL ANNULÉ";
    default:
      return recital.recordingOnly ? "ENREGISTREMENT" : "RÉCITAL";
  }
}

/**
 * « Vendredi 20 septembre à 19:00 », heure de Paris — ou rien quand la date
 * n'apprend plus rien : le récital est commencé, fini, annulé, ou il se joue
 * dès que l'interprète le décide.
 */
export function shareWhen(recital: RecitalSummary): string | null {
  if (recital.status !== "scheduled" || recital.immediate) {
    return null;
  }

  const at = new Date(recital.scheduledAt);

  if (Number.isNaN(at.getTime())) {
    return null;
  }

  const label = new Intl.DateTimeFormat("fr-FR", {
    timeZone: SHARE_TIME_ZONE,
    weekday: "long",
    day: "numeric",
    month: "long",
    hour: "2-digit",
    minute: "2-digit",
  }).format(at);

  return label.charAt(0).toUpperCase() + label.slice(1);
}

/** Le morceau annoncé, ou rien. */
export function sharePieceLine(recital: Pick<RecitalSummary, "piece">): string | null {
  if (recital.piece === null) {
    return null;
  }

  const composer = recital.piece.composer;

  return composer === null || composer === ""
    ? recital.piece.title
    : `${recital.piece.title} · ${composer}`;
}

/**
 * La phrase sous le titre, dans l'aperçu.
 *
 * Elle dit ce qu'il faut pour décider de cliquer : qui joue, quand, et à quelle
 * porte on arrive. Rien n'y promet une écoute sans compte : le direct demande
 * un compte (décidé le 2026-09-17), et c'est le replay qui s'ouvrira au lien
 * public. Une promesse fausse dans un aperçu se paie à la porte.
 */
export function shareDescription(recital: RecitalSummary): string {
  const who = performerName(recital);

  if (recital.status === "cancelled") {
    return "Ce récital a été annulé.";
  }

  if (recital.status === "ended") {
    return `${who} a joué sur Virtuosa. Ce récital est terminé.`;
  }

  if (recital.recordingOnly) {
    return `${who} joue sans public, pour être enregistré.`;
  }

  const door =
    recital.seats === null
      ? "Entre dans la salle."
      : "Les places sont comptées : réserve la tienne.";

  if (recital.status === "live") {
    return `${who} joue en ce moment sur Virtuosa. ${door}`;
  }

  const when = shareWhen(recital);

  return when === null
    ? `${who} au piano, d'un instant à l'autre, sur Virtuosa. ${door}`
    : `${who} au piano, ${lowerFirst(when)} (heure de Paris), sur Virtuosa. ${door}`;
}

/** Le titre de l'aperçu : le récital, et qui le joue. */
export function shareTitle(recital: RecitalSummary): string {
  return `${recital.title} · ${performerName(recital)} au piano`;
}

function lowerFirst(value: string): string {
  return value.charAt(0).toLowerCase() + value.slice(1);
}
